import { createContext, lazy, useState } from "react";

import type { Feature, IFeatureContext } from "./types";

const Calendar = lazy(() => import("../calendar"));
const Companies = lazy(() => import("../companies/CompanyList"));
const Contacts = lazy(() => import("../contacts/ContactList"));
const Documents = lazy(() => import("../documents/Documents"));
const Flightplan = lazy(() => import("../flightplan/Flightplan"));
const Inventory = lazy(() => import("../inventory/Inventory"));
const Jobs = lazy(() => import("../jobs/JobList"));
const Mitcom = lazy(() => import("../mitcom/Mitcom"));
const Orders = lazy(() => import("../orders/Orders"));
const Parts = lazy(() => import("../parts/Parts"));
const Quotes = lazy(() => import("../quotes/Quotes"));
const Tools = lazy(() => import("../tools/Tools"));
const UserManagement = lazy(() => import("../users/user-management/UserManagementList"));
const Settings = lazy(() => import("./Settings"));
const Dashboard = lazy(() => import("../dashboard/Dashboard"));
const DirectoryDashboard = lazy(() => import("../directory/DirectoryDashboard"));
const SalesDashboard = lazy(() => import("../sections/SalesDashboard"));
const PurchasingDashboard = lazy(() => import("../sections/PurchasingDashboard"));
const TechRecordsDashboard = lazy(() => import("../sections/TechRecordsDashboard"));
const MaintenanceDashboard = lazy(() => import("../sections/MaintenanceDashboard"));
const StoresDashboard = lazy(() => import("../sections/StoresDashboard"));
const FinanceDashboard = lazy(() => import("../sections/FinanceDashboard"));
const Journals = lazy(() => import("../journals/Journals"));
const SafetyQualityDashboard = lazy(
  () => import("../sections/SafetyQualityDashboard")
);
const SystemAdminDashboard = lazy(
  () => import("../sections/SystemAdminDashboard")
);
const Aircraft = lazy(() => import("../aircraft/Aircraft"));
const Fleet = lazy(() => import("../fleet"));
const AircraftRoster = lazy(() => import("../aircraft_roster"));
const PilotRoster = lazy(() => import("../pilot_roster"));
const Invoices = lazy(() => import("../invoices/Invoices"));
const DueDate = lazy(() => import("../DueDate/DueDate"));
const MetabaseRoutes = lazy(() => import("../routes/MetabaseRoutes"));
const FollowUp = lazy(() => import("../followups/FollowUp"));
const Sors = lazy(() => import("../sors/Sors"));
const SalesOrder = lazy(() => import("../salesorder/SalesOrderList"));
const Tasks = lazy(() => import("../tasks"));
const WorkOrders = lazy(() => import("../work-orders/SalesOrderList"));
const ActionRegister = lazy(() => import("../action_register/ActionRegister"));
const PartsOrders = lazy(() => import("../parts-orders/Orders"));
const ModuleLandingPage = lazy(() => import("../components/ModuleLandingPage"));
const XeroSettings = lazy(() => import("../xero/XeroSettings"));
const Transpond = lazy(() => import("../transpond"));
const Microsoft365 = lazy(() => import("../integrations/Microsoft365"));
const Sage300 = lazy(() => import("../integrations/Sage300"));
const Laserfiche = lazy(() => import("../integrations/Laserfiche"));


// Stubbed module landing pages — render ModuleLandingPage until the real module is built.
// See brain/projects/module-landing-pages.md for build plan per module.
const AccountsExports = lazy(() => import("../accounts-exports"));
const AccountsReports = lazy(() => import("../accounts-reports"));
const SystemReports = lazy(() => import("../system-reports"));
const SystemConfig = lazy(() => import("../system-config"));
const History = lazy(() => import("../history"));
const AuthorisedRequests = lazy(() => import("../authorised-requests"));
const ChartOfAccounts = lazy(() => import("../chart-of-accounts"));
const Currency = lazy(() => import("../currency"));
const BatchExports = lazy(() => import("../batchexports"));
const FinancialReports = lazy(() => import("../financial-reports"));
const OperationsReports = lazy(() => import("../operations-reports"));
const Groups = lazy(() => import("../groups"));
const Modules = lazy(() => import("../modules"));
const ReportsAndForms = lazy(() => import("../reports-and-forms"));
const Blank = lazy(() => import("../blank"));
const Onboarding = lazy(() => import("../onboarding"));
const Users = lazy(() => import("../users"));
const UserRates = lazy(() => import("../user-rates"));
const Vehicles = lazy(() => import("../vehicles"));
const ModSync = lazy(() => import("../modsync"));
const GroundEquipment = lazy(() => import("../ground-equipment"));
const ADsAndSBs = lazy(() => import("../ads-and-sbs"));
const AircraftTemplates = lazy(() => import("../aircraft-templates"));
const AircraftConfiguration = lazy(() => import("../aircraft-configuration"));
const AircraftServices = lazy(() => import("../aircraft-services"));
const ATA = lazy(() => import("../ata"));
const DefectReporting = lazy(() => import("../defect-reporting"));
const InspectionsTR = lazy(() => import("../inspections-tr"));
const LogBook = lazy(() => import("../log-book"));
const Modifications = lazy(() => import("../modifications"));
const RoleEquipment = lazy(() => import("../role-equipment"));
const MaintenanceLog = lazy(() => import("../maintenance-log"));
const TechLog = lazy(() => import("../tech-log"));
const Batches = lazy(() => import("../batches"));
const Certificates = lazy(() => import("../certificates"));
const Core = lazy(() => import("../core"));
const Flights = lazy(() => import("../flights"));
const FlightSchedule = lazy(() => import("../flight_schedule"));
const FlightFollowing = lazy(() => import("../flight_following"));
const FlightLoc = lazy(() => import("../flight_loc"));
const Landings = lazy(() => import("../landings"));
const FlightExpenses = lazy(() => import("../flight-expenses"));
const FlightDuty = lazy(() => import("../flight-duty"));
const FuelTanks = lazy(() => import("../fuel_tanks"));
const LoadSheet = lazy(() => import("../load_sheet"));
const Manifest = lazy(() => import("../manifest"));
const Trips = lazy(() => import("../trips"));
const Bookings = lazy(() => import("../bookings"));
const Freight = lazy(() => import("../freight"));
const InwardsGoods = lazy(() => import("../inwards-goods"));
const ServiceOrders = lazy(() => import("../service-orders"));
const CycleCount = lazy(() => import("../cycle-count"));
const ToolRequests = lazy(() => import("../tool-requests"));
const Audits = lazy(() => import("../audits"));
const FlightDocuments = lazy(() => import("../flight-documents"));
const RiskRegister = lazy(() => import("../risk-register"));
const SMS = lazy(() => import("../sms"));
const Personnel = lazy(() => import("../personnel"));
const Registers = lazy(() => import("../registers"));
// src/meetings is deliberately NOT imported: the module is parked behind
// ModuleLandingPage until /v6/meetings exists (see the Meeting entry below for
// the full reasoning). The directory is kept as the starting point for that
// build. Re-add this lazy import when the endpoint ships.
const Inspections = lazy(() => import("../inspections/Inspections"));
const Components = lazy(() => import("../component/Component"));
const Rmas = lazy(() => import("../rmas/Rmas"));
const Opportunities = lazy(() => import("../opportunities"));
const Projects = lazy(() => import("../projects"));
const Servers = lazy(() => import("../servers"));
const Subscriptions = lazy(() => import("../subscriptions"));
const Systems = lazy(() => import("../systems"));
const Timesheets = lazy(() => import("../timesheets"));
const SupplierInvoices = lazy(() => import("../supplier-invoices/SupplierInvoices"));

// preProd is all environment except test and prod
// const preProd = (env: string) => env !== "test" && env !== "prod";
// const reactEnv: string = process.env.REACT_APP_ENV as string;
// const isPreProd = preProd(reactEnv);

const UserPreferences = lazy(() => import("../users/UserPreferences"));
const HelpChat = lazy(() => import("../help-chat/HelpChat"));
const Trend = lazy(() => import("../trend"));
const Production = lazy(() => import("../production"));
const PartsRequisition = lazy(() => import("../parts-requisition"));
const Weather = lazy(() => import("../weather"));
const MobileEmulator = lazy(() => import("../mobile-emulator/MobileEmulator"));

const initialFeatures: Feature[] = [
  { name: "weather", component: Weather, isActive: true },
  // Mobile nav rows (mod_function `mobile-emulator`, view in url_query) —
  // replaced the client-injected Mustang "Mobile" section, 2026-09-26 (M4).
  // Without this entry the rows would fall back to their legacy `url`, an
  // off-tenant ?function= href. App.tsx maps them via mobileEmulatorTarget.
  { name: "mobile-emulator", component: MobileEmulator, isActive: true },
  { name: "pilots_weather", component: Weather, isActive: true },
  { name: "flight_weather", component: Weather, isActive: true },
  { name: "contacts", component: Contacts, isActive: true },
  // OPS Google Calendar module. Registered under both casings until the live
  // nav row's `function` is confirmed (seed: 2026-06-23-calendar-permissions);
  // prune to the canonical one after Task 11's DB lookup.
  { name: "calendar", component: Calendar, isActive: true },
  { name: "Calendar", component: Calendar, isActive: true },
  { name: "AddressBook", component: Companies, isActive: true },
  // Upstream e981cb245 normalised the module path to /addressbook (R55 alias).
  { name: "addressbook", component: Companies, isActive: true },
  { name: "companies", component: Companies, isActive: true },
  { name: "dashboard", component: Dashboard, isActive: true },
  {
    name: "inventory",
    component: Inventory,
    responsiveComponent: Parts,
    isActive: true,
  },
  {
    name: "jobs",
    component: Jobs,
    isActive: true,
  },
  {
    name: "mitcom",
    component: Mitcom,
    isActive: true,
  },
  { name: "user_management", component: UserManagement, isActive: true },
  { name: "transpond", component: Transpond, isActive: true },
  { name: "microsoft365", component: Microsoft365, isActive: true },
  { name: "sage300", component: Sage300, isActive: true },
  { name: "laserfiche", component: Laserfiche, isActive: true },

  {
    name: "user_settings",
    component: Settings,
    isActive: true,
  },
  {
    name: "flightplan",
    component: Flightplan,
    isActive: true,
  },
  {
    name: "tools",
    component: Tools,
    isActive: true,
  },
  {
    name: "quotes",
    component: Quotes,
    isActive: true,
  },
  {
    name: "supplier_quotes",
    component: Quotes,
    isActive: true,
  },
  {
    name: "supplier-quotes",
    component: Quotes,
    isActive: true,
  },
  {
    name: "customer_quotes",
    component: Quotes,
    isActive: true,
  },
  {
    name: "customer-quotes",
    component: Quotes,
    isActive: true,
  },
  {
    name: "orders",
    component: Orders,
    isActive: true,
  },
  {
    name: "documents",
    component: Documents,
    isActive: true
  },
  { name: "aircraft",    component: Aircraft,           isActive: true },
  { name: "fleet",       component: Fleet,              isActive: true },
  { name: "aircraft_roster", component: AircraftRoster, isActive: true },
  // Pilot Roster — nav row 10228 (Pilots section), mod_function=pilot_roster.
  // Registering it is what stops the item linking OFF-TENANT: with no active
  // entry here App.tsx never sets menuItem.to, so Menu.tsx fell back to the
  // legacy `menuItem.url` and sent the user to {global_site_domain}?function=
  // pilot_roster&nav=10228 — https://crm.aeronet.nz/ on g2, a different
  // install. Verified with scripts/nav/nav_reachability.py, 2026-08-29.
  { name: "pilot_roster", component: PilotRoster,   isActive: true },
  { name: "invoices",    component: Invoices,           isActive: true },
  { name: "supplier_invoices", component: SupplierInvoices, isActive: true },
  { name: "supplier-invoices", component: SupplierInvoices, isActive: true },
  { name: "whiteboard",  component: DueDate,            isActive: true },
  // Due Date Control — SCM nav row 517 carries mod_function=due_date_control;
  // same DueDate module as `whiteboard`, routed to /app/duedate in App.tsx.
  { name: "due_date_control", component: DueDate,       isActive: true },
  // Metabase Reporting — url nav row (remote_menu_key=metabase). The module's
  // internal navigation is absolute to /admin/metabase, so App.tsx maps the
  // nav entry to the existing admin/metabase/* mount (MetabaseRoutes).
  { name: "metabase",    component: MetabaseRoutes,     isActive: true },
  { name: "metabase_reports", component: MetabaseRoutes, isActive: true },
  { name: "metabase-reporting", component: MetabaseRoutes, isActive: true },
  { name: "followups",   component: FollowUp,           isActive: true },
  { name: "sor",           component: Sors,            isActive: true },
  { name: "salesorders",   component: SalesOrder,      isActive: true },
  // Modern tasks module (v6 port of legacy mod_tasks, Phase T1). `name` must
  // match the eventual `tasks` nav row mod_function. Feeds Work Orders later.
  { name: "tasks",         component: Tasks,           isActive: true },
  // Work Orders (MRO, Phase W) — verbatim clone of salesorder/ + Tasks tab,
  // same /v6/jobs backend. Nav row 145 ("Work Orders", mod_function=jobs,
  // parent Maintenance) is name-switched to this feature in App.tsx.
  { name: "workorders",    component: WorkOrders,      isActive: true },
  { name: "actionregister", component: ActionRegister, isActive: true },
  { name: "partsorders",   component: PartsOrders,     isActive: true },

  // Stub features — render ModuleLandingPage. Feature `name` matches the resolved
  // featureName from App.tsx (remote_menu_key || function). Replace component when
  // the real module is built. See brain/projects/module-landing-pages.md.
  { name: "myob_import",         component: AccountsExports,      isActive: true },
  { name: "reports",             component: SystemReports,        isActive: true },
  { name: "system_reports",      component: SystemReports,        isActive: true },
  { name: "system-reports",      component: SystemReports,        isActive: true },
  { name: "accounts_reports",    component: AccountsReports,      isActive: true },
  { name: "system_config",       component: SystemConfig,         isActive: true },
  { name: "system-config",       component: SystemConfig,         isActive: true },
  { name: "systemconfig",        component: SystemConfig,         isActive: true },
  { name: "config",              component: SystemConfig,         isActive: true },
  { name: "config2",             component: SystemConfig,         isActive: true },
  { name: "types",               component: SystemConfig,         isActive: true },
  { name: "history",             component: History,              isActive: true },
  { name: "authorised_request",  component: AuthorisedRequests,   isActive: true },
  { name: "accounts",            component: ChartOfAccounts,      isActive: true },
  { name: "currency",            component: Currency,             isActive: true },
  // Accounts Exports (H255): legacy myob_import, split out of Xero's Export Batches mode.
  { name: "batchexports",        component: BatchExports,         isActive: true },
  { name: "job_costs",           component: FinancialReports,     isActive: true },
  // Aeronet 5.0 nav remote_menu_key for the same UI (upstream 4206cdd67, R55 alias).
  { name: "financereports",      component: FinancialReports,     isActive: true },
  // Operations Reports (/app/operations-reports) — bookings + flight activity,
  // the ops-side sibling of job_costs. App.tsx also carries an explicit route so
  // the URL works before a per-tenant `modules` nav row is seeded; this entry is
  // what makes a seeded row resolve here instead of ModuleLandingPage.
  { name: "operations_reports",  component: OperationsReports,    isActive: true },
  { name: "groups",              component: Groups,               isActive: true },
  // Modules admin module (v6 port) — view/edit the modules table + the data-driven
  // home of the module-migration program (sidecar module_migration_status).
  // `name` matches the modules nav-row mod_function ('modules', id 3/10003).
  { name: "modules",             component: Modules,              isActive: true },
  { name: "module_stationery",   component: ReportsAndForms,      isActive: true },
  { name: "blank",               component: Blank,                isActive: true },
  { name: "onboarding",          component: Onboarding,           isActive: true },
  // tmp: design-spec workshop. Component is unreachable — App.tsx defines an
  // explicit <Route path="tmp/*" element={<TmpReload />}> that wins; this entry
  // just makes isFeatureActive("tmp") return true so the nav-mapper sets
  // menuItem.to = "tmp" instead of falling through to module-placeholder.
  { name: "tmp",                 component: ModuleLandingPage,    isActive: true },
  { name: "users",               component: Users,                isActive: true },
  // The live nav row (modules.id 10146) carries mod_function "time", and the whole
  // config family + permission token (time_admin) is seeded against "time" — so that
  // is the alias the mapper actually resolves. "timesheet"/"timesheets" are kept as
  // forward-compatible aliases.
  { name: "time",                component: Timesheets,           isActive: true },
  { name: "timesheet",           component: Timesheets,           isActive: true },
  { name: "timesheets",          component: Timesheets,           isActive: true },
  { name: "rates",               component: UserRates,            isActive: true },
  { name: "vehicles",            component: Vehicles,             isActive: true },
  { name: "modsync",             component: ModSync,              isActive: true },
  { name: "ground_equip",        component: GroundEquipment,      isActive: true },
  // Not a gate-pass any more. The three g2 nav rows that share this key (215
  // ADs and SBs, 267 Inspections, 10291 Modifications) used to land here on a
  // ModuleLandingPage "being migrated" stub while the three real modules sat
  // built and unreachable. This now routes to the mode shell that hosts all
  // three. The KEY IS DELIBERATELY UNCHANGED — mod_function is also the
  // config/permission token, and grants FK on modules.id, so aliasing the
  // component is safe where renaming the key would not be. Per-row landing
  // still needs a DB change; see src/aircraft-templates/index.tsx for why the
  // frontend structurally cannot do it.
  { name: "aircraft_templates",  component: AircraftTemplates,    isActive: true },
  { name: "ads_and_sbs",         component: ADsAndSBs,            isActive: true },
  { name: "inspections_tr",      component: InspectionsTR,        isActive: true },
  { name: "modifications",       component: Modifications,        isActive: true },
  { name: "aircraft_configuration", component: AircraftConfiguration, isActive: true },
  // Aircraft Services = the maintenance Log Book (`aircraft_services`) as a module: the
  // register, the one entry record and the tab grid both record tabs mount.
  //
  // `lbe` ("Log Book") opens it since Aircraft Patch 5 (Aaron 2026-10-09, closing AP-118).
  // Before, `lbe` opened src/log-book/, the pilot FLIGHT log (`log_book` table), so
  // Tech Records › Log Book showed the wrong domain. Measured 2026-10-09 (read-only): every
  // tenant's `lbe` rows are named "Log Book" under Tech Records (live and granted: 10298
  // on g2 / peak / mustang, 270 on demo), and no tenant uses `lbe` for a flight log. The
  // pilot Flight Logbook keeps its own key, `log-book` (below), at /app/log-book. The key
  // is unchanged, so no nav row moves. App.tsx also mounts `lbe/*` statically, so a
  // record tab can open an entry even where the nav does not list Log Book.
  { name: "aircraft_services", component: AircraftServices, isActive: true },
  { name: "ata",                 component: ATA,                  isActive: true },
  { name: "ata_tasks",           component: ATA,                  isActive: true },
  // Nav remote_menu_key spellings without separators (upstream ee36d50b1, R55 aliases).
  { name: "atachapters",         component: ATA,                  isActive: true },
  { name: "atatasks",            component: ATA,                  isActive: true },
  { name: "defect",              component: DefectReporting,      isActive: true },
  { name: "lbe",                 component: AircraftServices,     isActive: true },
  { name: "role_equip",          component: RoleEquipment,        isActive: true },
  { name: "maintenance_log",     component: MaintenanceLog,       isActive: true },
  { name: "techlog",             component: TechLog,              isActive: true },
  { name: "batches",             component: Batches,              isActive: true },
  { name: "invoice_batch",       component: Batches,              isActive: true },
  { name: "certs",               component: Certificates,         isActive: true },
  { name: "core",                component: Core,                 isActive: true },
  { name: "cores",               component: Core,                 isActive: true },
  // Maintenance → Tool Requests (/app/toolrequests).
  { name: "toolrequests",        component: ToolRequests,         isActive: true },
  // The same module under its cross-tenant mod_function: demo, crm and gch name
  // the nav row `service_requests` (g2 is being aligned to it). Route stays
  // /app/toolrequests — App.tsx maps both keys there.
  { name: "service_requests",    component: ToolRequests,         isActive: true },
  { name: "flights",             component: Flights,              isActive: true },
  { name: "flight_schedule",     component: FlightSchedule,       isActive: true },
  // Flight Following (v6 port of legacy mod_flight_following — the live
  // dispatch board). `name` matches the nav row mod_function
  // (`flight_following`, modules 514; url_query /app/flight_following/).
  { name: "flight_following",    component: FlightFollowing,      isActive: true },
  // Locations (v6 port of legacy mod_flight_loc — the location record kind).
  // `name` matches the nav row mod_function (`flight_loc`); url_query
  // /app/flight_loc/ so the route slug and module dir agree.
  { name: "flight_loc",          component: FlightLoc,            isActive: true },
  { name: "locations",           component: FlightLoc,            isActive: true },
  { name: "flight_locations",    component: FlightLoc,            isActive: true },
  // Landings — the LANDING record kind of that same legacy module ("Locations
  // and Landings" carried three kinds behind three nav_modes). `name` is the
  // PROPOSED nav row's mod_function (`landings`), which is also the SPA route
  // slug, the config/permission prefix and the /v6 router prefix.
  //
  // Registered here BEFORE the `modules` row exists, deliberately and in this
  // order: an entry here with no nav row is simply not linked from the menu,
  // whereas a nav row with no component does NOT 404 — Menu.tsx falls through
  // to an external href built from `global_site_domain`, which on g2 points at
  // another customer's install. Component first, nav row second.
  { name: "landings",            component: Landings,             isActive: true },
  // Flight Expenses — the EXPENSE record kind of that SAME `flight_landings`
  // table (source_type='expense', location_id=0, category in `notes`). Three
  // sibling kinds share one table; `landings` above owns the LANDING kind and
  // its predicate excludes this one by construction, so the two registers
  // cannot show each other's rows.
  //
  // `name` is the nav row's mod_function (`flight_expenses`, modules 10735),
  // which is also the SPA route slug, the config/permission prefix and the /v6
  // router prefix.
  //
  // ⚠️ NOT a bare `expenses` — but NOT for the reason this note used to give.
  // It said "config already carries `global_expenses_accounts`, so the short
  // name would collide in the config namespace". That was stated as measured
  // and is FALSE: `get_config_value` (app/config/crud.py:10-45) filters on
  // BOTH `config_function == function` AND the variable, in both branches, and
  // row 1654 is `config_function='global'` — so it could never have collided.
  // Disproved and corrected in this module's own index.tsx on 2026-08-30; this
  // file was missed, which left origin/main asserting the claim in one file and
  // disproving it in another. sections.ts then cited THIS note in good faith
  // and carried the dead reason forward, which is why it is corrected here
  // rather than only where it was first caught.
  //
  // The real reason the name stands: it says what the record is scoped to — an
  // expense on a FLIGHT, not a general-ledger expense — and it matches its
  // sibling `flight_landings` / `src/landings`. The DIRECTORY is
  // `flight-expenses` (hyphen) while the route key is `flight_expenses`
  // (underscore), deliberately, exactly as `authorised-requests`/
  // `authorised_request` and `cycle-count`/`stock_tracker` do.
  //
  // Registered here BEFORE the `modules` row existed — same order and same
  // reason as Landings directly above. That row has since LANDED: `modules`
  // 10735, mod_function `flight_expenses`, parent_id 508 (Flight Admin),
  // products 'scm,ops,g2' on module_g2_demo, measured 2026-08-30. Its parent is
  // under review (Aaron: move Expenses to the Finance menu, which is `modules`
  // 507 — NOT 10001, which carries no g2 token), so treat the parent above as
  // measured-at-a-moment. The nav row remains owned by the lane holding the
  // `modules` table; this lane still writes none.
  { name: "flight_expenses",     component: FlightExpenses,       isActive: true },
  // Flight & Duty (/app/flightduty) — the crew DUTY LOG, over the eight
  // purpose-built /v6/flightduty/* paths. Reads `flights` (the duty table);
  // the flight register is `flights_info` behind /v6/flights and is a
  // different module. See src/flight-duty/types.ts for that boundary.
  //
  // `name` is `flightduty` WITHOUT an underscore, and that is measured, not a
  // style choice. It is simultaneously the legacy mod_function, the v6 router
  // prefix (/v6/flightduty), the config_function of 25+ existing config rows,
  // and the mod_function of the modules row that ALREADY EXISTS on g2
  // (id 10203). App.tsx generates each module route from the live nav row's
  // mod_function and resolves it against this table by the same key, so
  // registering `flight_duty` here would simply never match — the row would
  // fall through to ModuleLandingPage. The DIRECTORY stays hyphenated
  // (`flight-duty`), exactly as `flight-expenses`/`flight_expenses` and
  // `authorised-requests`/`authorised_request` already differ.
  //
  // Registered here BEFORE the module is reachable — component first, nav row
  // second. An unregistered nav row does not 404: Menu.tsx falls through to an
  // EXTERNAL href from `global_site_domain`, which on g2 is
  // https://crm.aeronet.nz/, ANOTHER CUSTOMER'S INSTALL. THIS LANE WRITES NO
  // NAV ROWS.
  //
  // ⚠️ Do NOT reuse nav 518 — it was repointed to `operations_reports` on
  // 2026-08-30 and reusing it would break that module to fix this one
  // (sections.ts records the change and says so explicitly). The real blocker
  // is different and smaller: row 10203 exists, carries products `scm,ops,g2`
  // and is already granted to groups 1/7/38, but its parent is 10292 =
  // "Hidden Modules", which config `nav_parent_names_ignore` strips from the
  // nav entirely. Re-parenting that row is the whole mutation, and it belongs
  // to the lane that owns the `modules` table.
  { name: "flightduty",          component: FlightDuty,           isActive: true },
  { name: "flight_duty",         component: FlightDuty,           isActive: true },
  { name: "flight-duty",         component: FlightDuty,           isActive: true },
  // Fuel Tanks (v6 port of legacy mod_fuel_tanks — ground fuel assets).
  // `name` matches the nav row mod_function (`fuel_tanks`, modules 276/10276).
  { name: "fuel_tanks",          component: FuelTanks,            isActive: true },
  { name: "fuel",                component: FuelTanks,            isActive: true },
  { name: "fuel-tanks",          component: FuelTanks,            isActive: true },
  { name: "load_sheet",          component: LoadSheet,            isActive: true },
  // Pax Manifest (v6 port of legacy mod_manifest → flight_booking's
  // passenger_manifest mode). `name` matches the nav row mod_function
  // (`manifest`) — NB the demo DB also carries a legacy row 516
  // "Passenger Manifesting" (mod_function passenger_manifest) under Flight
  // Admin; the nav seed reconciles the two (pax-manifest ship-status doc).
  { name: "manifest",            component: Manifest,             isActive: true },
  { name: "passenger_manifest",  component: Manifest,             isActive: true },
  { name: "pax_manifest",        component: Manifest,             isActive: true },
  { name: "pax-manifest",        component: Manifest,             isActive: true },
  // Trips (v6 port of legacy mod_trips — vehicle/ground trips on
  // flight_booking_vehicles, served by app/bookings' /v6/bookings/trips).
  // `name` matches the nav row mod_function (`trips`, modules 305/10305 —
  // both currently parented to "Hidden Modules"; the nav seed re-parents 305
  // to Flight Admin 508 with url_query /app/trips/).
  { name: "trips",               component: Trips,                isActive: true },
  // Bookings (v6 port of legacy mod_flight_booking). `name` matches the nav
  // row mod_function (`flight_booking`); the row's url_query is /app/bookings/
  // so App.tsx maps menuItem.to = "bookings".
  { name: "flight_booking",      component: Bookings,             isActive: true },
  { name: "bookings",            component: Bookings,             isActive: true },
  { name: "booking",             component: Bookings,             isActive: true },
  { name: "flight-booking",      component: Bookings,             isActive: true },
  { name: "freight",             component: Freight,              isActive: true },
  { name: "inwards",             component: InwardsGoods,         isActive: true },
  { name: "tds",                 component: ServiceOrders,        isActive: true },
  { name: "stock_tracker",       component: CycleCount,           isActive: true },
  { name: "audits",              component: Audits,               isActive: true },
  { name: "flight_doc",          component: FlightDocuments,      isActive: true },
  { name: "risk",                component: RiskRegister,         isActive: true },
  { name: "ncr",                 component: SMS,                  isActive: true },
  // `modules` row 10252 was repointed to mod_function = 'sms' (batch-05) to
  // disambiguate SMS from OFIs (ncr) — that nav row needs its own feature key
  // or it 404s via the catch-all NotFound route. Same placeholder component
  // as ncr; menuItem.name still drives the on-page title (see App.tsx route
  // map), so the two remain visually distinct even though they share a stub.
  { name: "sms",                 component: SMS,                  isActive: true },
  { name: "personnel",           component: Personnel,            isActive: true },
  { name: "registers",           component: Registers,            isActive: true },
  // Safety & Quality → Meeting (nav row `meetings`, modules id 208, granted to
  // 3 groups on g2). This row had NO entry here, and an unregistered nav key
  // makes Menu.tsx:353-374 render <a href={menuItem.url}> instead of a router
  // link — with `url` built by get_nav from the config `global_site_domain`,
  // which on module_g2_demo is `https://crm.aeronet.nz/`. Clicking "Meeting"
  // therefore sent a g2 evaluator OFF-TENANT to another customer's legacy
  // login page. `name` matches the nav row's mod_function EXACTLY (`meetings`)
  // — this is the canonical key, not an alias, so nothing is orphaned.
  //
  // ⚠️ POINTED AT THE PLACEHOLDER, NOT AT src/meetings, 2026-08-23.
  // Registering the real module fixed the off-tenant link and replaced it with
  // something worse: src/meetings calls exactly ONE endpoint, /v6/meetings, and
  // that endpoint is served by NO tenant (verified against the live g2 spec, 470
  // paths, zero `meeting*` matches; also absent from origin/main's api.py). So
  // every operation fails — including Save. The Attendees tab tells the user
  // "Save the meeting before adding attendees", which is an instruction that
  // CANNOT be followed. A form that looks real and cannot save is a worse trap
  // than a dead link: the dead link is obviously broken, this wastes the
  // evaluator's time and then loses their work.
  //
  // This is the shape of mistake worth naming, because the diff that caused it
  // looked like a fix: I briefed the lane to prioritise Meeting BECAUSE its link
  // left the tenant, and "make the link stop leaving" is satisfied by any page at
  // all. Fixing a symptom can incentivise a worse outcome unless the acceptable
  // non-build outcome is stated. It is stated now: the placeholder.
  //
  // src/meetings is KEPT, not deleted — it is a real starting point once there is
  // a backend. Restore this to `component: Meetings` when BOTH hold: /v6/meetings
  // is mounted on the target's live spec, AND the shape is ported from the legacy
  // .nz Meetings screen (or signed off by Aaron). Prod v6 has no src/meetings at
  // all, so there is nothing to port from the SPA — the legacy screen is the only
  // source of truth for what this should look like.
  //
  // THE GENERAL RULE THIS EARNS (adopted from the conformance station): a module
  // may not be registered in Features.tsx while its primary endpoint is absent
  // from the target tenant's live spec. Reachable ⇒ shippable, as a merge gate,
  // not a review dimension. scripts/g2_module_health.py already produces exactly
  // this signal and already listed meetings as DEAD-API before it went live.
  { name: "meetings",            component: ModuleLandingPage,    isActive: true },
  { name: "inspections",         component: Inspections,          isActive: true },
  { name: "components",          component: Components,           isActive: true },
  { name: "rmas",                component: Rmas,                 isActive: true },
  { name: "opportunities",       component: Opportunities,        isActive: true },
  { name: "projects",            component: Projects,             isActive: true },
  { name: "servers",             component: Servers,              isActive: true },
  { name: "subscriptions",       component: Subscriptions,        isActive: true },
  { name: "systems",             component: Systems,              isActive: true },

  // Section landings — each nav dropdown's home row lands on one of these.
  { name: "directory",     component: DirectoryDashboard, isActive: true },
  { name: "sales",         component: SalesDashboard, isActive: true },
  { name: "purchasing",    component: PurchasingDashboard, isActive: true },
  { name: "tech-records",  component: TechRecordsDashboard, isActive: true },
  { name: "maintenance",   component: MaintenanceDashboard, isActive: true },
  { name: "stores",        component: StoresDashboard, isActive: true },
  { name: "finance",       component: FinanceDashboard, isActive: true },
  { name: "safety-quality", component: SafetyQualityDashboard, isActive: true },
  { name: "system-admin",  component: SystemAdminDashboard, isActive: true },

  { name: "coming_soon",   component: ModuleLandingPage, isActive: true },
  { name: "users_prefs", component: UserPreferences, isActive: true },
  { name: "help_chat", component: HelpChat, isActive: true },
  { name: "maintenance-log", component: MaintenanceLog, isActive: true },
  { name: "maintenancelog", component: MaintenanceLog, isActive: true },
  { name: "aircraft_modifications", component: Modifications, isActive: true },
  { name: "role_equipment", component: RoleEquipment, isActive: true },
  { name: "role-equipment", component: RoleEquipment, isActive: true },
  { name: "roleequipment", component: RoleEquipment, isActive: true },
  { name: "tech-log", component: TechLog, isActive: true },
  { name: "tech_log", component: TechLog, isActive: true },
  { name: "trend", component: Trend, isActive: true },
  { name: "engine-trend", component: Trend, isActive: true },
  { name: "engine_trend", component: Trend, isActive: true },
  { name: "module-placeholder/Trend", component: Trend, isActive: true },
  { name: "time-sheets", component: Timesheets, isActive: true },
  { name: "production", component: Production, isActive: true },
  { name: "production-planning", component: Production, isActive: true },
  { name: "parts-requisition", component: PartsRequisition, isActive: true },
  { name: "partsrequisitions", component: PartsRequisition, isActive: true },
  // --- g2 nav-key aliases (2026-08-23) ---------------------------------------
  // DEFAULT_G2_NAV in App.tsx emits `function` values that did not match the
  // `name` keys below, and `name` IS the mounted route path — so each of these
  // nav rows pointed past a module that already existed. Aliasing follows the
  // precedent immediately above (role-equipment / tech-log are each
  // registered under several spellings).
  //
  // ALIAS, NEVER RENAME. mod_function is simultaneously the route key AND the
  // config/permission key: renaming the canonical key on the left would orphan
  // every grant and permission token issued against it.
  { name: "work-orders", component: WorkOrders, isActive: true },
  { name: "parts-orders", component: PartsOrders, isActive: true },
  { name: "ads-and-sbs", component: ADsAndSBs, isActive: true },
  { name: "defect-reporting", component: DefectReporting, isActive: true },
  { name: "ground-equipment", component: GroundEquipment, isActive: true },
  { name: "ata-chapters", component: ATA, isActive: true },
  { name: "ata-tasks", component: ATA, isActive: true },
  { name: "log-book", component: LogBook, isActive: true },
  { name: "xero", component: XeroSettings, isActive: true },
  { name: "finance/xero", component: XeroSettings, isActive: true },
  { name: "journals", component: Journals, isActive: true },
  { name: "finance/journals", component: Journals, isActive: true },
  { name: "inwards-goods", component: InwardsGoods, isActive: true },
  { name: "inwardsgoods", component: InwardsGoods, isActive: true },
];

export const FeatureContext = createContext<IFeatureContext>(
  {} as IFeatureContext
);

export const FeatureProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [features] = useState<Feature[]>(initialFeatures);

  const getFeature = (featureName: string): Feature | undefined => {
    return features.find((i) => i.name === featureName);
  };

  const isFeatureActive = (featureName: string): boolean => {
    const feature = getFeature(featureName);
    const isActive = !!feature && feature.isActive;
    console.debug(`${featureName} ${isActive ? "is active" : "is disabled"}`);
    return isActive;
  };

  return (
    <FeatureContext.Provider value={{ features, isFeatureActive, getFeature }}>
      {children}
    </FeatureContext.Provider>
  );
};
